import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  TableContainer,
  Table,
  TableData,
  TableRow,
  FilterContainer,
  ClearFilters,
  DropdownToggleStyled,
  DropdownItemContent,
  IconWrapper,
  ButtonSeparator,
  StickySelectHeader,
  StickySelectBody
} from './layout';
import { ACTION_COLUMNS, useColumnOrder } from './hooks/useColumnOrder';

import { AiOutlineClear } from 'react-icons/ai';
import { DraggableTh } from './components/draggableTh';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Input,
  Row,
  Col,
  ListGroup,
  ListGroupItem
} from 'reactstrap';
import { PhoneCell } from 'components/FlexTable/components/cells/phone';
import { BiFilterAlt } from 'react-icons/bi';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { tableTheme } from './layout/theme';

/**
 * @typedef {Object} Column
 * @property {string} dataField
 * @property {string} text
 * @property {Function} [formatter]
 * @property {Object} [style]
 * @property {Object} [headerStyle]
 * @property {boolean} [sort]
 */

/**
 * @typedef {Object} SortConfig
 * @property {string} key
 * @property {string} direction
 */

/**
 * @param {Object} props
 * @param {string} [props.entityName]
 * @param {string} [props.classes]
 * @param {Function} props.setSortConfig
 * @param {SortConfig} props.sortConfig
 * @param {Column[]} props.columns
 * @param {Object[]} props.data
 * @param {string} props.keyField
 * @param {boolean} [props.bootstrap4]
 * @param {boolean} [props.bordered]
 * @param {string} [props.rowClasses]
 * @param {boolean} [props.showFilterContainer]
 * @param {boolean} [props.selectRow]
 * @param {Function} [props.onSelect]
 * @param {Function} [props.onSelectAll]
 * @param {Array} [props.selectedRows]
 * @param {Object|Function} [props.rowStyle]
 */
const FlexTable = ({
  classes,
  setSortConfig,
  sortConfig,
  entityName,
  showFilterContainer = true,
  selectRow = false,
  onSelect,
  onSelectAll,
  selectedRows = [],
  rowStyle = {},
  isSLA = false,
  slaInfo = null,
  ...props
}) => {
  const { columns } = props;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [allSelected, setAllSelected] = useState(false);

  const {
    columnOrder,
    columnWidths,
    handleResize,
    moveColumn,
    requestSort,
    resetFilter,
    visibleColumns,
    handleColumnVisibilityChange
  } = useColumnOrder(columns, setSortConfig, entityName);

  const sortedData = useMemo(() => {
    if (!sortConfig?.key) return props.data;

    const sorted = [...props.data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      const aVal = typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
      const bVal = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;

      if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [props.data, sortConfig]);

  const handleResizeColumn = useCallback((index, width) => {
    handleResize(index, width);
  });

  const handleSelectAll = useCallback(
    (e) => {
      const isSelected = e.target.checked;
      setAllSelected(isSelected);
      if (onSelectAll) {
        onSelectAll(isSelected, sortedData);
      }
    },
    [onSelectAll, sortedData]
  );

  const handleSelectRow = useCallback(
    (row, e) => {
      if (onSelect) {
        onSelect(row, e.target.checked);
      }
    },
    [onSelect]
  );

  const isRowSelected = useCallback(
    (row) => {
      return selectedRows.some(
        (selectedRow) => selectedRow[props.keyField] === row[props.keyField]
      );
    },
    [selectedRows, props.keyField]
  );

  const getRowStyle = useCallback(
    (row) => {
      if (typeof rowStyle === 'function') return rowStyle(row);
      return rowStyle;
    },
    [rowStyle]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      {showFilterContainer && (
        <FilterContainer>
          <ClearFilters
            onClick={() => {
              resetFilter();
            }}
            className="small"
          >
            <AiOutlineClear /> Limpar visualização
          </ClearFilters>
          <ButtonSeparator />
          <Dropdown
            direction="right"
            onClick={() => {
              setDropdownOpen(!dropdownOpen);
            }}
            isOpen={dropdownOpen}
            toggle={() => setDropdownOpen(!dropdownOpen)}
          >
            <DropdownToggleStyled outline={true}>
              <BiFilterAlt size={16} color={tableTheme.colors.primary} />
            </DropdownToggleStyled>

            <DropdownMenu onClick={(e) => e.stopPropagation()} positionFixed>
              <DropdownItem header>Visualizar colunas</DropdownItem>
              <DropdownItem divider />
              {columns
                .filter((col) => !ACTION_COLUMNS.includes(col.dataField))
                .map((col) => (
                  <DropdownItem
                    key={col.dataField}
                    toggle={() => setDropdownOpen(!dropdownOpen)}
                    onClick={(e) => {
                      handleColumnVisibilityChange(col.dataField);
                      e.stopPropagation();
                    }}
                  >
                    <DropdownItemContent
                      onClick={(e) => {
                        handleColumnVisibilityChange(col.dataField);
                        e.stopPropagation();
                      }}
                    >
                      {visibleColumns.includes(col.dataField) ? (
                        <LuEye
                          onClick={(e) => {
                            handleColumnVisibilityChange(col.dataField);
                            e.stopPropagation();
                          }}
                        />
                      ) : (
                        <LuEyeOff
                          onClick={(e) => {
                            handleColumnVisibilityChange(col.dataField);
                            e.stopPropagation();
                          }}
                        />
                      )}
                      <IconWrapper
                        onClick={(e) => {
                          handleColumnVisibilityChange(col.dataField);
                          e.stopPropagation();
                        }}
                      >
                        {col.text}
                      </IconWrapper>
                    </DropdownItemContent>
                  </DropdownItem>
                ))}
            </DropdownMenu>
          </Dropdown>
        </FilterContainer>
      )}
      <TableContainer>
        <Table className={classes}>
          <thead>
            <TableRow>
              {selectRow && (
                <StickySelectHeader>
                  <Input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    style={{ margin: 'auto' }}
                  />
                </StickySelectHeader>
              )}
              {columnOrder.map((column, index) =>
                visibleColumns.includes(column.dataField) ||
                ACTION_COLUMNS.includes(column.dataField) ? (
                  <DraggableTh
                    key={column.dataField}
                    column={column}
                    index={index}
                    moveColumn={moveColumn}
                    requestSort={requestSort}
                    sortConfig={sortConfig}
                    onResize={handleResizeColumn}
                    columnWidths={columnWidths}
                    columnOrder={columnOrder}
                  >
                    {column.text}
                  </DraggableTh>
                ) : null
              )}
            </TableRow>
          </thead>
          <tbody>
            {sortedData.map((row, rowIndex) => (
              <TableRow
                key={row[props.keyField] || rowIndex}
                className={isRowSelected(row) ? 'table-active' : ''}
                style={getRowStyle(row)}
              >
                {selectRow && (
                  <StickySelectBody>
                    <Input
                      type="checkbox"
                      checked={isRowSelected(row)}
                      onChange={(e) => handleSelectRow(row, e)}
                      style={{ margin: 'auto' }}
                    />
                  </StickySelectBody>
                )}
                {columnOrder.map(
                  (column, colIndex) =>
                    (visibleColumns.includes(column.dataField) ||
                      ACTION_COLUMNS.includes(column.dataField)) && (
                      <TableData
                        key={column.dataField}
                        style={{
                          ...column.style,
                          width: ACTION_COLUMNS.includes(column.dataField)
                            ? 80
                            : columnWidths[colIndex]
                        }}
                      >
                        {(() => {
                          const content = column.formatter
                            ? column.formatter(row[column.dataField], row)
                            : row[column.dataField];

                          if (React.isValidElement(content)) return content;

                          if (content && typeof content === 'object') {
                            const maybeDdi =
                              content.ddi || content.DDI || content.ddiCode;
                            const maybeNumber =
                              content.number || content.phone || content.value;
                            if (maybeDdi && maybeNumber) {
                              if (
                                String(maybeDdi).trim() &&
                                String(maybeNumber).trim()
                              ) {
                                return (
                                  <PhoneCell
                                    phone={maybeNumber}
                                    ddi={maybeDdi}
                                  />
                                );
                              }
                              return null;
                            }
                            return null;
                          }

                          return content;
                        })()}
                      </TableData>
                    )
                )}
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      {isSLA && slaInfo && (
        <div className="mb-3 mt-3">
          <Row>
            <Col md="3">
              <ListGroup flush>
                <ListGroupItem disabled style={{ padding: '5px 0 0 20px' }}>
                  <span style={{ fontSize: 13, fontWeight: 'bold' }}>
                    Protocolos conformes:
                  </span>{' '}
                  {slaInfo.conformingProtocols || 0}
                </ListGroupItem>
                <ListGroupItem disabled style={{ padding: '5px 0 0 20px' }}>
                  <span style={{ fontSize: 13, fontWeight: 'bold' }}>
                    Protocolos não conformes:
                  </span>{' '}
                  {slaInfo.nonConformingProtocols || 0}
                </ListGroupItem>
                <ListGroupItem disabled style={{ padding: '5px 0 0 20px' }}>
                  <span style={{ fontSize: 13, fontWeight: 'bold' }}>
                    Média de Atendimento:
                  </span>{' '}
                  {slaInfo.averageSLAServiceTime !== null &&
                  slaInfo.averageSLAServiceTime !== undefined
                    ? `${slaInfo.averageSLAServiceTime}%`
                    : 'N/A'}
                </ListGroupItem>
                <ListGroupItem disabled style={{ padding: '5px 0 0 20px' }}>
                  <span style={{ fontSize: 13, fontWeight: 'bold' }}>
                    Média de Solução:
                  </span>{' '}
                  {slaInfo.averageSLASolutionTime !== null &&
                  slaInfo.averageSLASolutionTime !== undefined
                    ? `${slaInfo.averageSLASolutionTime}%`
                    : 'N/A'}
                </ListGroupItem>
              </ListGroup>
            </Col>
          </Row>
        </div>
      )}
    </DndProvider>
  );
};

FlexTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataField: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      formatter: PropTypes.func,
      style: PropTypes.object,
      headerStyle: PropTypes.object,
      sort: PropTypes.bool
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  keyField: PropTypes.string.isRequired,
  bootstrap4: PropTypes.bool,
  bordered: PropTypes.bool,
  classes: PropTypes.string,
  rowClasses: PropTypes.string,
  setSortConfig: PropTypes.func.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string
  }).isRequired,
  showFilterContainer: PropTypes.bool,
  selectRow: PropTypes.bool,
  onSelect: PropTypes.func,
  onSelectAll: PropTypes.func,
  selectedRows: PropTypes.array,
  rowStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  isSLA: PropTypes.bool,
  slaInfo: PropTypes.shape({
    conformingProtocols: PropTypes.number,
    nonConformingProtocols: PropTypes.number,
    averageSLAServiceTime: PropTypes.number, // (%)
    averageSLASolutionTime: PropTypes.number // (%)
  })
};

export default FlexTable;
